import React, { useEffect, useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle, RefreshCw, ShieldCheck } from 'lucide-react';
type FormData = {
  name: string;
  email: string;
  subject: string;
  message: string;
  captchaAnswer: string;
};
export function ContactPage() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    subject: '',
    message: '',
    captchaAnswer: ''
  });
  const [captchaNumbers, setCaptchaNumbers] = useState<[number, number]>([0, 0]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Generate random numbers for captcha on mount
  useEffect(() => {
    generateCaptcha();
  }, []);
  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    setCaptchaNumbers([num1, num2]);
    setFormData(prev => ({
      ...prev,
      captchaAnswer: ''
    }));
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user types
    if (error) setError(null);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Basic validation
    if (!formData.name || !formData.email || !formData.subject || !formData.message || !formData.captchaAnswer) {
      setError('Please fill in all fields.');
      return;
    }
    // Captcha validation
    const sum = captchaNumbers[0] + captchaNumbers[1];
    if (parseInt(formData.captchaAnswer) !== sum) {
      setError('Incorrect math answer. Please try again.');
      generateCaptcha();
      return;
    }
    // Success
    setIsSubmitted(true);
    setError(null);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  return <div className="bg-white min-h-screen pb-20" data-id="element-293">
      {/* Page Header */}
      <div className="bg-gray-50 border-b border-gray-200 py-16 px-4 sm:px-6 lg:px-8" data-id="element-294">
        <div className="max-w-4xl mx-auto text-center" data-id="element-295">
          <div className="flex items-center justify-center gap-3 mb-4" data-id="element-296">
            <Mail className="w-10 h-10 text-[#0e7be1]" data-id="element-297" />
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900" data-id="element-298">
              Contact Us
            </h1>
          </div>
          <div className="w-24 h-1 bg-[#0e7be1] mx-auto rounded-full" data-id="element-299"></div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-300">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12" data-id="element-301">
          {/* Left Column: Contact Info */}
          <div className="lg:col-span-2 space-y-6" data-id="element-302">
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 lg:p-8 h-full" data-id="element-303">
              <h2 className="text-xl font-bold text-gray-900 mb-1" data-id="element-304">
                Best Vitamins For All
              </h2>
              <p className="text-[#0e7be1] font-medium mb-8" data-id="element-305">GNLD NEO LIFE</p>

              <div className="space-y-6" data-id="element-306">
                <div className="flex items-start gap-4" data-id="element-307">
                  <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center flex-shrink-0 text-[#0e7be1]" data-id="element-308">
                    <MapPin className="w-5 h-5" data-id="element-309" />
                  </div>
                  <div data-id="element-310">
                    <p className="text-sm font-medium text-gray-500 mb-1" data-id="element-311">
                      Location
                    </p>
                    <p className="text-gray-900 font-medium" data-id="element-312">
                      Boca Raton, Florida
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-313">
                  <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center flex-shrink-0 text-[#0e7be1]" data-id="element-314">
                    <Phone className="w-5 h-5" data-id="element-315" />
                  </div>
                  <div data-id="element-316">
                    <p className="text-sm font-medium text-gray-500 mb-1" data-id="element-317">
                      Toll Free
                    </p>
                    <a href="tel:800-260-2808" className="text-gray-900 font-medium hover:text-[#0e7be1] transition-colors" data-id="element-318">
                      (800) 260-2808
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-319">
                  <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center flex-shrink-0 text-[#0e7be1]" data-id="element-320">
                    <Mail className="w-5 h-5" data-id="element-321" />
                  </div>
                  <div data-id="element-322">
                    <p className="text-sm font-medium text-gray-500 mb-1" data-id="element-323">
                      Email
                    </p>
                    <a href="mailto:sales@bestvitaminsforall.com" className="text-gray-900 font-medium hover:text-[#0e7be1] transition-colors break-all" data-id="element-324">
                      sales@bestvitaminsforall.com
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Contact Form */}
          <div className="lg:col-span-3" data-id="element-325">
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 lg:p-8" data-id="element-326">
              {isSubmitted ? <div className="text-center py-12" data-id="element-327">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 text-green-600" data-id="element-328">
                    <CheckCircle className="w-8 h-8" data-id="element-329" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-330">
                    Message Sent!
                  </h3>
                  <p className="text-gray-600 mb-8" data-id="element-331">
                    Thank you for your message! We will get back to you shortly.
                  </p>
                  <button onClick={() => {
                setIsSubmitted(false);
                setFormData({
                  name: '',
                  email: '',
                  subject: '',
                  message: '',
                  captchaAnswer: ''
                });
                generateCaptcha();
              }} className="inline-flex items-center text-[#0e7be1] font-medium hover:underline" data-id="element-332">
                    Send another message
                  </button>
                </div> : <>
                  <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-333">
                    Send Us a Message
                  </h2>

                  <form onSubmit={handleSubmit} className="space-y-5" data-id="element-334">
                    <div data-id="element-335">
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-336">
                        Your Name <span className="text-red-500" data-id="element-337">*</span>
                      </label>
                      <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-200 rounded-lg text-gray-900 focus:ring-2 focus:ring-[#0e7be1] focus:border-transparent outline-none transition bg-gray-50 focus:bg-white" placeholder="John Doe" data-id="element-338" />
                    </div>

                    <div data-id="element-339">
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-340">
                        Email Address <span className="text-red-500" data-id="element-341">*</span>
                      </label>
                      <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-200 rounded-lg text-gray-900 focus:ring-2 focus:ring-[#0e7be1] focus:border-transparent outline-none transition bg-gray-50 focus:bg-white" placeholder="john@example.com" data-id="element-342" />
                    </div>

                    <div data-id="element-343">
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-344">
                        Subject <span className="text-red-500" data-id="element-345">*</span>
                      </label>
                      <input type="text" id="subject" name="subject" value={formData.subject} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-200 rounded-lg text-gray-900 focus:ring-2 focus:ring-[#0e7be1] focus:border-transparent outline-none transition bg-gray-50 focus:bg-white" placeholder="How can we help?" data-id="element-346" />
                    </div>

                    <div data-id="element-347">
                      <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-348">
                        Message <span className="text-red-500" data-id="element-349">*</span>
                      </label>
                      <textarea id="message" name="message" value={formData.message} onChange={handleChange} required rows={5} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-gray-900 focus:ring-2 focus:ring-[#0e7be1] focus:border-transparent outline-none transition bg-gray-50 focus:bg-white resize-none" placeholder="Type your message here..." data-id="element-350"></textarea>
                    </div>

                    {/* Math Captcha */}
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-100" data-id="element-351">
                      <div className="flex items-center gap-2 mb-3" data-id="element-352">
                        <ShieldCheck className="w-5 h-5 text-[#0e7be1]" data-id="element-353" />
                        <span className="text-sm font-bold text-gray-900" data-id="element-354">
                          Security Check
                        </span>
                      </div>
                      <div className="flex items-center gap-4" data-id="element-355">
                        <label htmlFor="captchaAnswer" className="text-sm font-medium text-gray-700 whitespace-nowrap" data-id="element-356">
                          What is{' '}
                          <span className="font-bold text-lg" data-id="element-357">
                            {captchaNumbers[0]} + {captchaNumbers[1]}
                          </span>
                          ?
                        </label>
                        <div className="relative w-24" data-id="element-358">
                          <input type="number" id="captchaAnswer" name="captchaAnswer" value={formData.captchaAnswer} onChange={handleChange} required className="w-full px-3 py-2 border border-gray-300 rounded-md text-center font-bold text-gray-900 focus:ring-2 focus:ring-[#0e7be1] focus:border-transparent outline-none" data-id="element-359" />
                        </div>
                        <button type="button" onClick={generateCaptcha} className="text-gray-400 hover:text-[#0e7be1] transition-colors" title="Refresh captcha" data-id="element-360">
                          <RefreshCw className="w-5 h-5" data-id="element-361" />
                        </button>
                      </div>
                    </div>

                    {error && <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg border border-red-100" data-id="element-362">
                        {error}
                      </div>}

                    <button type="submit" className="w-full bg-[#0e7be1] hover:bg-[#0c6bc4] text-white font-bold text-lg px-6 py-3 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2" data-id="element-363">
                      Send Message
                      <Send className="w-5 h-5" data-id="element-364" />
                    </button>
                  </form>
                </>}
            </div>
          </div>
        </div>
      </div>
    </div>;
}